import { zodResolver } from '@hookform/resolvers/zod';
import { format, set } from 'date-fns';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';
import { IEvent } from '@clickersphere/core/entities/IEvent';
import { ResponseError } from '@clickersphere/core/entities/ResponseError';
import {
  EventImageConsentEnum,
  EVENT_IMAGE_CONSENT_VALUES,
  toEventImageConsent,
} from '@clickersphere/core/enums/EventImageConsentEnum';
import { SupportedLanguageEnum } from '@clickersphere/core/enums/SupportedLanguageEnum';
import { useBirths } from '../../../../app/hooks/birth/useBirths';
import { useEventChecklists } from '../../../../app/hooks/event-checklist/useEventChecklists';
import { useEventStatus } from '../../../../app/hooks/event-status/useEventStatus';
import { useCreateEvent } from '../../../../app/hooks/event/useCreateEvent';
import { useUpdateEvent } from '../../../../app/hooks/event/useUpdateEvent';
import { useLeads } from '../../../../app/hooks/lead/useLeads';
import { useAuth } from '../../../../app/hooks/useAuth';
import { useError } from '../../../../app/hooks/useError';
import useToast from '../../../../app/hooks/useToast';
import { useStore } from '../../../../app/store';
import {
  EN_US_DATE_FORMAT,
  parseDate,
  PT_BR_DATE_FORMAT,
} from '@clickersphere/core/utils/parseDate';
import {
  parseDateTo12h,
  parseTimeTo24h,
} from '@clickersphere/core/utils/parseTime';
import { eventImageConsentOptionKeys } from '../../Event/eventEnumLabels';
import useLanguageSelectorController from '../../LanguageSelector/useLanguageSelectorController';

interface IEventDraft {
  leadId?: string;
  statusId?: string;
  description?: string;
  locationId?: string | null;
}

interface IUseRegisterEventForm {
  event?: IEvent;
  leadId?: string;
  eventStatusId?: string;
  eventDraft?: IEventDraft;
}

export function useRegisterEventFormController({
  event,
  leadId,
  eventStatusId,
  eventDraft,
}: IUseRegisterEventForm) {
  const [isTimePickerVisible, setIsTimePickerVisible] =
    useState<boolean>(false);
  const [selectedLocationName, setSelectedLocationName] = useState<
    string | undefined
  >(event?.location);
  const { handleCloseModal } = useStore(
    useShallow((state) => ({
      handleCloseModal: state.modal.handleCloseModal,
    })),
  );
  const { toast } = useToast();
  const { handleError } = useError();
  const { t } = useTranslation();
  const { tenant } = useAuth();
  const { leads, isLeadsFetching } = useLeads();
  const { eventStatus } = useEventStatus();
  const { eventChecklists } = useEventChecklists();
  const { createEventAsync, createEventIsPending } = useCreateEvent();
  const { updateEventAsync, updateEventIsPending } = useUpdateEvent();

  const { selectedLanguage } = useLanguageSelectorController();
  const dateFormat =
    selectedLanguage === SupportedLanguageEnum['PT-BR']
      ? PT_BR_DATE_FORMAT
      : EN_US_DATE_FORMAT;

  const schema = z.object({
    leadId: z
      .string()
      .nonempty(t('register.errors.name.required'))
      .refine((v) => v !== 'none', {
        message: t('register.errors.name.required'),
      }),
    description: z
      .string()
      .nonempty(t('register.errors.description.required'))
      .max(100, t('register.errors.description.invalid')),
    date: z
      .string()
      .optional()
      .refine(
        (value) => {
          if (!value || value.trim() === '') return true;
          return !!parseDate(value, dateFormat);
        },
        { message: t('register.errors.date.invalid') },
      ),
    time: z.string().optional(),
    locationId: z.string().nullish(),
    birthId: z.string().nullish(),
    statusId: z.string().uuid(),
    imageConsent: z.enum(EVENT_IMAGE_CONSENT_VALUES),
    checklistId: z.string().optional(),
  });

  type FormData = z.infer<typeof schema>;

  const outputSchema = schema.transform((data) => {
    const { date, time, locationId } = data;

    const formatDate = (raw: string) => {
      const parsed = parseDate(raw, dateFormat);
      return parsed ? parsed.toISOString() : undefined;
    };

    const formatTimeTo24h = (rawTime: string) => {
      const [hour, rest] = rawTime.split(':');
      const [minute, period] = rest.split(' ');
      return parseTimeTo24h({ hour, minute, period: period as 'AM' | 'PM' });
    };

    let finalDate = date ? formatDate(date) : undefined;

    if (finalDate && time) {
      const timeIn24h = formatTimeTo24h(time);
      const [h, m] = timeIn24h.split(':');
      finalDate = set(new Date(finalDate), {
        hours: Number(h),
        minutes: Number(m),
      }).toISOString();
    }

    return {
      ...data,
      date: finalDate,
      locationId: locationId || null,
      location: locationId ? selectedLocationName : undefined,
      birthId: data.birthId || null,
      time: undefined,
    };
  });

  const {
    handleSubmit: hookFormHandleSubmit,
    register,
    formState: { errors },
    control,
    reset,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      ...event,
      leadId: event?.lead?.id || leadId || eventDraft?.leadId,
      statusId: event?.statusId || eventStatusId || eventDraft?.statusId,
      description: event?.description ?? eventDraft?.description,
      locationId: event?.locationId ?? eventDraft?.locationId,
      birthId: event?.birthId ?? null,
      imageConsent: toEventImageConsent(event?.imageConsent),
      date:
        event?.date &&
        format(
          event.date,
          selectedLanguage === SupportedLanguageEnum['PT-BR']
            ? PT_BR_DATE_FORMAT
            : EN_US_DATE_FORMAT,
        ),
      time: event?.date && parseDateTo12h(event.date),
    },
  });

  const birthPhotographerMode = tenant?.birthPhotographerMode ?? false;
  const watchedLeadId = watch('leadId');
  const birthLeadId =
    watchedLeadId && watchedLeadId !== 'none' ? watchedLeadId : undefined;
  const { births } = useBirths(
    birthLeadId,
    birthPhotographerMode && !!birthLeadId,
  );

  const birthOptions = births.map((birth) => {
    const date = birth.expectedBirthDate
      ? format(
          new Date(`${birth.expectedBirthDate.slice(0, 10)}T00:00:00`),
          dateFormat,
        )
      : t('birth.card.noExpectedBirthDate');

    return {
      value: birth.id,
      text: birth.babyName
        ? t('birth.event.optionWithBaby', { babyName: birth.babyName, date })
        : t('birth.event.optionWithoutBaby', { date }),
    };
  });

  const handleSubmit = hookFormHandleSubmit(async (data) => {
    try {
      const { checklistId, birthId, ...parsedData } = outputSchema.parse(data);

      const processedData = birthPhotographerMode
        ? { ...parsedData, birthId }
        : parsedData;

      if (!event) {
        await createEventAsync({
          ...processedData,
          checklistId: checklistId || undefined,
        });
        toast(t('register.toast.event.created'));
      } else {
        await updateEventAsync({
          id: event.id,
          data: { ...processedData },
        });
        toast(t('register.toast.event.updated'));
      }
      handleCloseModal();
    } catch (error) {
      if (error instanceof ResponseError) {
        handleError(error);
      } else {
        toast(t('register.toast.event.error'), 'error');
      }
    }
  });

  function toggleTimePickerVisibility() {
    setIsTimePickerVisible((prevState) => !prevState);
  }

  const dateValue = watch('date');
  const trimmedDate = dateValue?.trim() ?? '';

  const selectedLeadId = event?.lead?.id ?? leadId;
  const leadOptions = [
    { value: 'none', text: t('register.fields.leadNone') },
    ...(leads ?? [])
      .filter((lead) => lead.active || lead.id === selectedLeadId)
      .map((lead) => ({ value: lead.id, text: lead.name })),
  ];

  return {
    isFetching: createEventIsPending || updateEventIsPending || isLeadsFetching,
    errors,
    control,
    t,
    leadOptions,
    eventStatus: eventStatus ?? [],
    eventChecklists,
    isEditing: !!event,
    checklistOptions: eventChecklists.map((checklist) => ({
      value: checklist.id,
      text: checklist.name,
    })),
    isTimePickerVisible,
    birthPhotographerMode,
    birthOptions,
    imageConsentOptions: EVENT_IMAGE_CONSENT_VALUES.map(
      (consent: EventImageConsentEnum) => ({
        value: consent,
        text: t(eventImageConsentOptionKeys[consent]),
      }),
    ),
    dateField: {
      empty: trimmedDate === '',
      invalid: trimmedDate !== '' && !parseDate(trimmedDate, dateFormat),
    },
    handleSubmit,
    register,
    toggleTimePickerVisibility,
    handleFormReset: reset,
    setSelectedLocationName,
  };
}
